import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ShoppingBag, FileText, FlaskConical, Snowflake, Package, ShieldCheck, Minus, Plus, Star, ArrowLeft, CheckCircle2, Truck, ClipboardCheck, Sparkles } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ProductCard } from '../components/ProductCard';
import { PRODUCTS } from '../components/products';
import { CTA } from '../components/CTA';
import { Vial } from '../components/Vial';
import { useCart, BULK_TIERS, getDiscountForQty } from '../components/cart/CartContext';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/Accordion';
const TRUST_ITEMS = [{
  icon: FlaskConical,
  label: 'HPLC tested'
}, {
  icon: Snowflake,
  label: 'Cold-chain'
}, {
  icon: Package,
  label: 'Discreet UK'
}, {
  icon: ShieldCheck,
  label: 'Money-back'
}];
const SHIPMENT_ITEMS = [{
  icon: FlaskConical,
  label: 'Sealed vial'
}, {
  icon: ClipboardCheck,
  label: 'Batch COA'
}, {
  icon: Snowflake,
  label: 'Cold-chain pack'
}, {
  icon: Truck,
  label: 'Tracked dispatch'
}];
function Stars({
  count = 5
}: {
  count?: number;
}) {
  return <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`} data-id="element-1415">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < count ? 'fill-primary text-primary' : 'fill-border text-border'}`} strokeWidth={0} data-id="element-1416" />)}
    </div>;
}
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = useMemo(() => PRODUCTS.find(p => p.slug === slug), [slug]);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [showMobileBar, setShowMobileBar] = useState(false);
  const buyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
    setQty(1);
    setAdded(false);
  }, [slug]);
  useEffect(() => {
    if (!buyRef.current) return;
    const obs = new IntersectionObserver(([entry]) => setShowMobileBar(!entry.isIntersecting), {
      rootMargin: '-100px 0px 0px 0px',
      threshold: 0
    });
    obs.observe(buyRef.current);
    return () => obs.disconnect();
  }, [slug]);
  if (!product) {
    return <div className="min-h-screen w-full bg-background" data-id="element-1417">
        <AnnouncementBar data-id="element-1418" />
        <Header data-id="element-1419" />
        <main className="container mx-auto py-32 text-center" data-id="element-1420">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1421">
            404
          </p>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground" data-id="element-1422">
            Peptide not found
          </h1>
          <p className="mt-4 text-muted-foreground" data-id="element-1423">
            That product doesn't exist or may have been removed.
          </p>
          <div className="mt-8 flex justify-center" data-id="element-1424">
            <CTA to="/shop" variant="primary" icon={<ArrowLeft className="h-4 w-4" strokeWidth={2} data-id="element-1426" />} iconPosition="left" data-id="element-1425">
              Back to shop
            </CTA>
          </div>
        </main>
        <Footer data-id="element-1427" />
      </div>;
  }
  const related = PRODUCTS.filter(p => p.id !== product.id && p.goals.some(g => product.goals.includes(g))).slice(0, 3);
  const {
    addItem
  } = useCart();
  const handleAdd = () => {
    addItem(product.id, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };
  const discount = getDiscountForQty(qty);
  const lineGross = product.price * qty;
  const lineTotal = lineGross * (1 - discount);
  const perVialPrice = product.price * (1 - discount);
  return <div className="min-h-screen w-full bg-background" data-id="element-1428">
      <AnnouncementBar data-id="element-1429" />
      <Header data-id="element-1430" />

      <main data-id="element-1431">
        {/* Breadcrumb */}
        <div className="w-full border-b border-border" data-id="element-1432">
          <div className="container mx-auto py-5" data-id="element-1433">
            <nav className="flex items-center gap-1.5 text-[12px] text-muted-foreground" aria-label="Breadcrumb" data-id="element-1434">
              <Link to="/" className="hover:text-foreground transition-colors" data-id="element-1435">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1436" />
              <Link to="/shop" className="hover:text-foreground transition-colors" data-id="element-1437">
                Shop
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1438" />
              <span className="text-foreground" data-id="element-1439">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Product hero */}
        <section className="container mx-auto pt-8 lg:pt-12 pb-20 lg:pb-28" data-id="element-1440">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-start" data-id="element-1441">
            {/* LEFT — Visual + What ships */}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:sticky lg:top-24" data-id="element-1442">
              {/* Hero visual */}
              <div className="relative w-full aspect-[4/5] sm:aspect-square rounded-3xl overflow-hidden bg-secondary/50 border border-border" data-id="element-1443">
                <div className="absolute inset-5 rounded-2xl bg-card border border-border" data-id="element-1444" />

                <div className="absolute inset-0 flex items-center justify-center text-foreground" data-id="element-1445">
                  <motion.div animate={{
                  y: [0, -4, 0]
                }} transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1446">
                    <Vial label={product.name} doseMg={product.doseMg} volumeMl={product.volumeMl} category={product.category} size={360} data-id="element-1447" />
                  </motion.div>
                </div>

                {product.badge && <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full font-medium text-[10px] uppercase tracking-[0.06em] px-2.5 py-1 backdrop-blur-sm border bg-background/95 border-border text-foreground" data-id="element-1448">
                    {product.badge === 'Bestseller' && <span className="h-1 w-1 rounded-full bg-primary" data-id="element-1449" />}
                    {product.badge === 'New' && <span className="h-1 w-1 rounded-full bg-primary" data-id="element-1450" />}
                    {product.badge}
                  </span>}

                <div className="hidden sm:flex absolute top-8 right-5 items-center gap-3" data-id="element-1451">
                  <span className="hidden md:block h-px w-8 bg-foreground/15" data-id="element-1452" />
                  <div className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-1453">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1454">
                      Purity
                    </div>
                    <div className="font-mono text-base text-foreground font-medium leading-none mt-0.5" data-id="element-1455">
                      {product.purity.toFixed(1)}%
                    </div>
                  </div>
                </div>

                <div className="hidden sm:flex absolute bottom-7 left-5 items-center gap-3" data-id="element-1456">
                  <div className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-1457">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1458">
                      Batch
                    </div>
                    <div className="font-mono text-[13px] text-foreground font-medium leading-none mt-0.5" data-id="element-1459">
                      {product.batch}
                    </div>
                  </div>
                  <span className="hidden md:block h-px w-8 bg-foreground/15" data-id="element-1460" />
                </div>

                {/* NEW: Storage spec at bottom-right */}
                <div className="hidden sm:flex absolute bottom-7 right-5 items-center gap-3" data-id="element-1461">
                  <span className="hidden md:block h-px w-8 bg-foreground/15" data-id="element-1462" />
                  <div className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm text-right" data-id="element-1463">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1464">
                      Storage
                    </div>
                    <div className="font-mono text-[13px] text-foreground font-medium leading-none mt-0.5 [font-feature-settings:'tnum']" data-id="element-1465">
                      2–8°C
                    </div>
                  </div>
                </div>

                {/* NEW: Contents mass annotation at middle-right */}
                <div className="hidden sm:flex absolute top-1/2 -translate-y-1/2 right-5 items-center gap-3" data-id="element-1466">
                  <span className="hidden md:block h-px w-8 bg-foreground/15" data-id="element-1467" />
                  <div className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm text-right" data-id="element-1468">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1469">
                      Contents
                    </div>
                    <div className="font-mono text-[13px] font-medium leading-none mt-0.5 [font-feature-settings:'tnum']" data-id="element-1470">
                      <span className="text-foreground" data-id="element-1471">{product.doseMg}</span>
                      <span className="text-primary font-semibold" data-id="element-1472">mg</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* What ships */}
              <div className="mt-5 rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1473">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-border" data-id="element-1474">
                  <div className="flex items-center gap-2.5" data-id="element-1475">
                    <div className="h-4 w-4 text-foreground" strokeWidth={1.75} data-id="element-1476" />
                    <span className="text-[13px] font-semibold tracking-tight text-foreground" data-id="element-1477">
                      Every order includes
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-primary font-semibold" data-id="element-1478">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1479" />
                    Ships today
                  </span>
                </div>
                <ul className="divide-y divide-border" data-id="element-1480">
                  {SHIPMENT_ITEMS.map(({
                  icon: Icon,
                  label
                }, i) => <li key={label} className="flex items-center gap-3 px-5 py-3" data-id="element-1481">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-1482">
                        <Icon className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} data-id="element-1483" />
                      </div>
                      <span className="text-[13px] text-foreground flex-1" data-id="element-1484">
                        {label}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground" data-id="element-1485">
                        0{i + 1}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>

            {/* RIGHT — Details */}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.08,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col" data-id="element-1486">
              <div className="flex items-center gap-3 mb-5" data-id="element-1487">
                <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground px-2.5 py-1 rounded-full border border-border bg-card" data-id="element-1488">
                  {product.category}
                </span>
                {product.inStock ? <span className="inline-flex items-center gap-1.5 text-[11px] text-primary font-medium" data-id="element-1489">
                    <span className="relative flex h-1.5 w-1.5" data-id="element-1490">
                      <span className="absolute inset-0 rounded-full bg-primary animate-ping opacity-60" data-id="element-1491" />
                      <span className="relative h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1492" />
                    </span>
                    In stock · ships today
                  </span> : <span className="text-[11px] text-muted-foreground" data-id="element-1493">
                    Out of stock
                  </span>}
              </div>

              <h1 className="text-4xl lg:text-[44px] font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-1494">
                {product.name}
              </h1>
              <p className="mt-3 text-[16px] text-muted-foreground leading-relaxed max-w-lg" data-id="element-1495">
                {product.tagline}
              </p>

              {/* Reviews row — tight inline */}
              <div className="mt-5 flex items-center gap-2.5 text-[12px]" data-id="element-1496">
                <Stars count={5} data-id="element-1497" />
                <span className="font-mono text-foreground font-medium [font-feature-settings:'tnum']" data-id="element-1498">
                  4.9
                </span>
                <a href="#reviews" className="font-mono text-muted-foreground hover:text-foreground underline underline-offset-2 decoration-border hover:decoration-foreground transition-colors [font-feature-settings:'tnum']" data-id="element-1499">
                  184 reviews
                </a>
              </div>

              {/* PRICE + CONTENTS — tight hero buy block */}
              <div className="mt-8 flex items-baseline gap-3" data-id="element-1500">
                <span className="font-mono text-[56px] font-medium text-foreground tracking-[-0.025em] leading-[0.95] [font-feature-settings:'tnum']" data-id="element-1501">
                  £{product.price}
                </span>
                <span className="text-border text-[32px] font-light leading-none" aria-hidden data-id="element-1502">
                  /
                </span>
                <span className="font-mono text-[28px] font-medium tracking-[-0.02em] leading-none [font-feature-settings:'tnum']" data-id="element-1503">
                  <span className="text-foreground" data-id="element-1504">{product.doseMg}</span>
                  <span className="text-primary" data-id="element-1505">mg</span>
                </span>
              </div>
              <div className="mt-2 font-mono text-[12.5px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-1506">
                £{(product.price / product.doseMg).toFixed(2)} per mg ·
                Lyophilised vial
              </div>

              {/* Compact lab verification strip — purity, batch, COA link */}
              <div className="mt-5 flex items-center flex-wrap gap-x-4 gap-y-2 px-4 py-3 rounded-xl border border-border bg-card" data-id="element-1507">
                <span className="inline-flex items-center gap-1.5" data-id="element-1508">
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" strokeWidth={2.5} data-id="element-1509" />
                  <span className="text-[12px] font-semibold text-foreground" data-id="element-1510">
                    Lab verified
                  </span>
                </span>
                <span className="text-border" aria-hidden data-id="element-1511">
                  ·
                </span>
                <span className="font-mono text-[12px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-1512">
                  <span className="text-foreground font-semibold" data-id="element-1513">
                    {product.purity.toFixed(1)}%
                  </span>{' '}
                  HPLC purity
                </span>
                <span className="text-border" aria-hidden data-id="element-1514">
                  ·
                </span>
                <span className="font-mono text-[12px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-1515">
                  Batch <span className="text-foreground" data-id="element-1516">{product.batch}</span>
                </span>
                <a href="#coa" className="ml-auto inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors group" data-id="element-1517">
                  <FileText className="h-3 w-3" strokeWidth={1.75} data-id="element-1518" />
                  View COA
                  <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} data-id="element-1519" />
                </a>
              </div>

              <p className="mt-3 text-[12px] text-muted-foreground" data-id="element-1520">
                Free UK delivery on orders over{' '}
                <span className="font-mono text-foreground" data-id="element-1521">£75</span> ·
                Same-day dispatch before 2pm
              </p>

              {/* Researched for */}
              <div className="mt-7" data-id="element-1522">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2.5" data-id="element-1523">
                  Researched for
                </div>
                <div className="flex flex-wrap gap-1.5" data-id="element-1524">
                  {product.goals.map(g => <span key={g} className="inline-flex items-center px-3 py-1 rounded-full bg-secondary text-foreground text-[12px] font-medium border border-border" data-id="element-1525">
                      {g}
                    </span>)}
                </div>
              </div>

              {/* Purchase options — unified bulk picker */}
              <div className="mt-8 rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1526">
                {/* Header */}
                <div className="flex items-end justify-between gap-4 px-5 py-4 border-b border-border" data-id="element-1527">
                  <div className="text-[15px] font-semibold tracking-tight text-foreground" data-id="element-1528">
                    How many vials?
                  </div>
                  {discount > 0 ? <div className="text-right" data-id="element-1529">
                      <div className="font-mono text-[16px] font-semibold text-primary leading-none [font-feature-settings:'tnum']" data-id="element-1530">
                        −£{(product.price * qty - lineTotal).toFixed(0)}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mt-1" data-id="element-1531">
                        Saving on this order
                      </div>
                    </div> : <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1532">
                      Save up to{' '}
                      <span className="text-primary font-semibold" data-id="element-1533">20%</span>
                    </div>}
                </div>

                {/* Options list */}
                <ul className="divide-y divide-border" data-id="element-1534">
                  {BULK_TIERS.map(tier => {
                  const active = qty === tier.qty;
                  const tierPerVial = product.price * (1 - tier.discount);
                  const tierTotal = tierPerVial * tier.qty;
                  const tierSavings = product.price * tier.qty - tierTotal;
                  const isPopular = tier.qty === 3;
                  const isBestValue = tier.qty === 5;
                  const subLabel = tier.qty === 1 ? 'Standard one-time order' : tier.qty === 3 ? 'Most researchers pick this' : tier.qty === 5 ? 'Best per-vial value' : 'Maximum savings, long-term research';
                  return <li key={tier.qty} data-id="element-1535">
                        <button onClick={() => setQty(tier.qty)} className={`relative w-full flex items-center gap-4 px-5 py-4 text-left transition-colors ${active ? 'bg-primary/[0.05]' : 'hover:bg-secondary/40'}`} data-id="element-1536">
                          {/* Radio dot */}
                          <span className={`relative h-[18px] w-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${active ? 'border-primary' : 'border-border'}`} aria-hidden data-id="element-1537">
                            {active && <span className="h-2 w-2 rounded-full bg-primary" data-id="element-1538" />}
                          </span>

                          {/* Label */}
                          <div className="flex-1 min-w-0" data-id="element-1539">
                            <div className="flex items-center gap-2 flex-wrap" data-id="element-1540">
                              <span className="text-[14px] font-semibold tracking-tight text-foreground leading-tight" data-id="element-1541">
                                {tier.qty === 1 ? 'Single vial' : `${tier.qty}-vial bundle`}
                              </span>
                              {isPopular && <span className="text-[9px] uppercase tracking-[0.08em] font-bold px-1.5 py-0.5 rounded-full bg-foreground text-background leading-none" data-id="element-1542">
                                  Most popular
                                </span>}
                              {isBestValue && <span className="text-[9px] uppercase tracking-[0.08em] font-bold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground leading-none" data-id="element-1543">
                                  Best value
                                </span>}
                            </div>
                            <div className="text-[11.5px] text-muted-foreground mt-1 leading-tight" data-id="element-1544">
                              {subLabel}
                            </div>
                          </div>

                          {/* Pricing */}
                          <div className="text-right flex-shrink-0" data-id="element-1545">
                            <div className="font-mono text-[15px] font-semibold text-foreground leading-none [font-feature-settings:'tnum']" data-id="element-1546">
                              £{tierPerVial.toFixed(0)}
                              <span className="text-[10.5px] font-normal text-muted-foreground ml-0.5" data-id="element-1547">
                                /vial
                              </span>
                            </div>
                            <div className="font-mono text-[10.5px] mt-1 leading-none [font-feature-settings:'tnum']" data-id="element-1548">
                              {tierSavings > 0 ? <span className="text-primary font-semibold" data-id="element-1549">
                                  Save £{tierSavings.toFixed(0)}
                                </span> : <span className="text-muted-foreground/60" data-id="element-1550">
                                  £{tierTotal.toFixed(0)} total
                                </span>}
                            </div>
                          </div>
                        </button>
                      </li>;
                })}
                </ul>

                {/* Footer hint */}
                <div className="px-5 py-3 border-t border-border bg-secondary/30 text-[11.5px] text-muted-foreground" data-id="element-1551">
                  Free UK delivery on orders over{' '}
                  <span className="font-semibold text-foreground" data-id="element-1552">£75</span> ·
                  Same-day dispatch before 2pm
                </div>
              </div>

              {/* Qty + Buy */}
              <div ref={buyRef} className="mt-5 flex items-stretch gap-3" data-id="element-1553">
                <div className="inline-flex items-center rounded-full border border-border bg-card overflow-hidden" data-id="element-1554">
                  <button onClick={() => setQty(q => Math.max(1, q - 1))} className="h-[50px] w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Decrease quantity" data-id="element-1555">
                    <Minus className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-1556" />
                  </button>
                  <span className="font-mono text-[15px] w-9 text-center text-foreground select-none" data-id="element-1557">
                    {qty}
                  </span>
                  <button onClick={() => setQty(q => Math.min(10, q + 1))} className="h-[50px] w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Increase quantity" data-id="element-1558">
                    <Plus className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-1559" />
                  </button>
                </div>
                <CTA variant="accent" size="lg" onClick={handleAdd} disabled={!product.inStock} fullWidth icon={added ? <CheckCircle2 className="h-[15px] w-[15px]" strokeWidth={2.25} data-id="element-1561" /> : <ShoppingBag className="h-[15px] w-[15px]" strokeWidth={2} data-id="element-1562" />} iconPosition="left" className="flex-1" data-id="element-1560">
                  {added ? <>
                      Added <span className="opacity-70 mx-1.5" data-id="element-1563">·</span>
                      <span className="font-mono" data-id="element-1564">£{lineTotal.toFixed(0)}</span>
                    </> : <>
                      Add to cart <span className="opacity-70 mx-1.5" data-id="element-1565">·</span>
                      {discount > 0 && <span className="font-mono opacity-70 line-through mr-1.5" data-id="element-1566">
                          £{lineGross.toFixed(0)}
                        </span>}
                      <span className="font-mono" data-id="element-1567">£{lineTotal.toFixed(0)}</span>
                    </>}
                </CTA>
              </div>

              {/* Frequently researched together — compact suggestion */}
              <PairedPeptidesSection product={product} data-id="element-1568" />

              {/* Trust strip */}
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 border-t border-border" data-id="element-1569">
                {TRUST_ITEMS.map(({
                icon: Icon,
                label
              }) => <div key={label} className="flex items-center gap-2" data-id="element-1570">
                    <Icon className="h-4 w-4 text-primary flex-shrink-0" strokeWidth={1.75} data-id="element-1571" />
                    <span className="text-[12px] font-medium text-foreground" data-id="element-1572">
                      {label}
                    </span>
                  </div>)}
              </div>

              {/* Accordion */}
              <div className="mt-10" data-id="element-1573">
                <Accordion type="single" defaultValue="desc" className="w-full" data-id="element-1574">
                  <AccordionItem value="desc" className="border-border" data-id="element-1575">
                    <AccordionTrigger className="text-left text-base font-medium hover:no-underline" data-id="element-1576">
                      Description
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground" data-id="element-1577">
                      {product.name} is sold for in-vitro laboratory research
                      use only. Each vial contains{' '}
                      <span className="font-mono text-foreground" data-id="element-1578">
                        {product.doseMg}mg
                      </span>{' '}
                      of lyophilised peptide with measured purity of{' '}
                      <span className="font-mono text-foreground" data-id="element-1579">
                        {product.purity.toFixed(1)}%
                      </span>{' '}
                      by HPLC. Identity confirmed by mass spectrometry.
                      Manufactured to research-grade standards and verified
                      against batch{' '}
                      <span className="font-mono text-foreground" data-id="element-1580">
                        {product.batch}
                      </span>{' '}
                      documentation.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="recon" className="border-border" data-id="element-1581">
                    <AccordionTrigger className="text-left text-base font-medium hover:no-underline" data-id="element-1582">
                      Reconstitution
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground space-y-3" data-id="element-1583">
                      <p data-id="element-1584">
                        Recommended reconstitution with bacteriostatic water
                        (BAC water) for laboratory research applications. Add
                        diluent slowly down the side of the vial — do not shake.
                        Gently swirl until fully dissolved.
                      </p>
                      <p data-id="element-1585">
                        Typical reconstitution:{' '}
                        <span className="font-mono text-foreground" data-id="element-1586">2ml</span>{' '}
                        BAC water per{' '}
                        <span className="font-mono text-foreground" data-id="element-1587">
                          {product.doseMg}mg
                        </span>{' '}
                        vial. Specific reconstitution volumes depend on the
                        research protocol.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="storage" className="border-border" data-id="element-1588">
                    <AccordionTrigger className="text-left text-base font-medium hover:no-underline" data-id="element-1589">
                      Storage
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground space-y-2" data-id="element-1590">
                      <p data-id="element-1591">
                        Lyophilised: store at{' '}
                        <span className="font-mono text-foreground" data-id="element-1592">2–8°C</span>
                        . Stable for up to 24 months from manufacture date.
                      </p>
                      <p data-id="element-1593">
                        Reconstituted: refrigerate at{' '}
                        <span className="font-mono text-foreground" data-id="element-1594">2–8°C</span>{' '}
                        and use within 28 days. Avoid freeze-thaw cycles.
                        Protect from light.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="compliance" className="border-border" data-id="element-1595">
                    <AccordionTrigger className="text-left text-base font-medium hover:no-underline" data-id="element-1596">
                      Compliance & disclaimer
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground" data-id="element-1597">
                      Sold for in-vitro laboratory and research use only. Not
                      for human consumption, medical, veterinary, or diagnostic
                      use. peptides.ml makes no medical claims. You must be 18
                      or over to purchase. By purchasing you confirm you are a
                      qualified researcher acting in compliance with UK law.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="shipping" className="border-border" data-id="element-1598">
                    <AccordionTrigger className="text-left text-base font-medium hover:no-underline" data-id="element-1599">
                      Shipping & returns
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground space-y-2" data-id="element-1600">
                      <p data-id="element-1601">
                        Same-day UK dispatch on orders placed before{' '}
                        <span className="font-mono text-foreground" data-id="element-1602">2pm</span>{' '}
                        (Mon–Fri). Tracked cold-chain courier delivery in plain,
                        discreet packaging.
                      </p>
                      <p data-id="element-1603">
                        30-day money-back guarantee on unopened vials. Damaged
                        or compromised shipments replaced free of charge.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </motion.div>
          </div>
        </section>

        {/* COA section */}
        <section id="coa" className="w-full bg-foreground text-background" data-id="element-1604">
          <div className="container mx-auto py-20 lg:py-24" data-id="element-1605">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1606">
              <motion.div initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5
            }} data-id="element-1607">
                <p className="text-[11px] uppercase tracking-wider text-background/60 font-medium mb-3" data-id="element-1608">
                  Batch <span className="font-mono" data-id="element-1609">{product.batch}</span>
                </p>
                <h2 className="text-3xl lg:text-5xl font-semibold tracking-[-0.025em] text-background" data-id="element-1610">
                  This batch, independently verified.
                </h2>
                <p className="mt-5 text-[16px] leading-relaxed text-background/70 max-w-lg" data-id="element-1611">
                  This {product.name} batch was tested by an ISO
                  17025-accredited UK laboratory. The full chromatogram and
                  analytical report is available below.
                </p>
                <div className="mt-7 flex flex-wrap gap-3" data-id="element-1612">
                  <CTA href="#" variant="primary" size="lg" icon={<FileText className="h-[15px] w-[15px]" strokeWidth={2} data-id="element-1614" />} iconPosition="left" className="!bg-background !text-foreground hover:!bg-background/95" data-id="element-1613">
                    Download full COA
                  </CTA>
                  <CTA href="#" variant="ghost" size="lg" withArrow className="!text-background !bg-transparent border border-background/20 hover:!bg-background/10" data-id="element-1615">
                    Verify batch
                  </CTA>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} data-id="element-1616">
                <div className="rounded-2xl bg-background text-foreground p-7 lg:p-9 border border-background/10" data-id="element-1617">
                  <div className="flex items-center justify-between pb-5 border-b border-border" data-id="element-1618">
                    <div className="flex items-center gap-3" data-id="element-1619">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-1620">
                        <FileText className="h-4 w-4 text-primary" strokeWidth={1.75} data-id="element-1621" />
                      </div>
                      <div data-id="element-1622">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1623">
                          Certificate of Analysis
                        </div>
                        <div className="text-sm font-semibold" data-id="element-1624">
                          {product.name} · Batch{' '}
                          <span className="font-mono" data-id="element-1625">{product.batch}</span>
                        </div>
                      </div>
                    </div>
                    <span className="font-mono text-[11px] text-muted-foreground" data-id="element-1626">
                      12 Apr 2026
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-5 py-6 border-b border-border" data-id="element-1627">
                    <CoaRow label="Purity (HPLC)" value={`${product.purity.toFixed(1)}%`} large data-id="element-1628" />
                    <CoaRow label="Net peptide content" value={`${(product.doseMg + 0.02).toFixed(2)}mg`} large data-id="element-1629" />
                    <CoaRow label="Identity (MS)" value="Confirmed" data-id="element-1630" />
                    <CoaRow label="Endotoxin" value="< 0.05 EU/mg" data-id="element-1631" />
                    <CoaRow label="Water content" value="< 5.0%" data-id="element-1632" />
                    <CoaRow label="Appearance" value="White lyo." data-id="element-1633" />
                  </div>

                  <div className="pt-6" data-id="element-1634">
                    <div className="flex items-center justify-between mb-3" data-id="element-1635">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1636">
                        HPLC chromatogram
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground" data-id="element-1637">
                        Retention 8.41 min
                      </span>
                    </div>
                    <svg viewBox="0 0 400 100" className="w-full h-24" preserveAspectRatio="none" data-id="element-1638">
                      <defs data-id="element-1639">
                        <pattern id="grid-pdp" width="40" height="20" patternUnits="userSpaceOnUse" data-id="element-1640">
                          <path d="M 40 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" data-id="element-1641" />
                        </pattern>
                      </defs>
                      <rect width="400" height="100" fill="url(#grid-pdp)" className="text-foreground" data-id="element-1642" />
                      <path d="M0,90 L60,90 L80,88 L120,86 L150,84 L170,80 L185,60 L195,15 L205,15 L215,55 L230,82 L260,86 L300,88 L340,89 L400,90" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" data-id="element-1643" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Often stacked with */}
        {related.length > 0 && <section className="w-full py-20 lg:py-24 border-t border-border" data-id="element-1644">
            <div className="container mx-auto" data-id="element-1645">
              <div className="flex items-end justify-between gap-6 mb-10" data-id="element-1646">
                <div data-id="element-1647">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1648">
                    Researchers also stack
                  </p>
                  <h2 className="text-3xl lg:text-4xl font-semibold tracking-[-0.025em] text-foreground" data-id="element-1649">
                    Often researched alongside {product.name}.
                  </h2>
                </div>
                <CTA to="/shop" variant="ghost" size="md" withArrow className="hidden md:inline-flex" data-id="element-1650">
                  View all peptides
                </CTA>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1651">
                {related.map(p => <ProductCard key={p.id} product={p} detailed data-id="element-1652" />)}
              </div>
            </div>
          </section>}
      </main>

      <Footer data-id="element-1653" />

      {/* Mobile sticky buy bar */}
      <AnimatePresence data-id="element-1654">
        {showMobileBar && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-md" data-id="element-1655">
            <div className="container mx-auto py-3 flex items-center gap-3" data-id="element-1656">
              <div className="flex-1 min-w-0" data-id="element-1657">
                <div className="text-[13px] font-semibold text-foreground truncate" data-id="element-1658">
                  {product.name}
                </div>
                <div className="font-mono text-[12px] text-muted-foreground" data-id="element-1659">
                  £{product.price} <span className="text-border mx-1" data-id="element-1660">·</span>{' '}
                  {product.purity.toFixed(1)}%
                </div>
              </div>
              <CTA variant="accent" size="md" onClick={handleAdd} disabled={!product.inStock} icon={added ? <CheckCircle2 className="h-[15px] w-[15px]" strokeWidth={2.25} data-id="element-1662" /> : <ShoppingBag className="h-[15px] w-[15px]" strokeWidth={2} data-id="element-1663" />} iconPosition="left" data-id="element-1661">
                {added ? 'Added' : `Add · £${lineTotal.toFixed(0)}`}
              </CTA>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function LabSpec({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="px-4 py-3 border-r border-border last:border-r-0" data-id="element-1664">
      <div className="font-mono text-base font-medium text-foreground tracking-tight" data-id="element-1665">
        {value}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1666">
        {label}
      </div>
    </div>;
}
function CoaRow({
  label,
  value,
  large
}: {
  label: string;
  value: string;
  large?: boolean;
}) {
  return <div data-id="element-1667">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1668">
        {label}
      </div>
      <div className={`font-mono mt-1 text-foreground ${large ? 'text-2xl font-medium' : 'text-base'}`} data-id="element-1669">
        {value}
      </div>
    </div>;
}
function PairedPeptidesSection({
  product
}: {
  product: (typeof PRODUCTS)[number];
}) {
  const {
    addItem
  } = useCart();
  const paired = useMemo(() => PRODUCTS.filter(p => p.id !== product.id && p.goals.some(g => product.goals.includes(g)) && p.inStock).slice(0, 2), [product.id, product.goals]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(paired.map(p => p.id)));
  const [added, setAdded] = useState(false);
  if (paired.length === 0) return null;
  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const selectedItems = paired.filter(p => selected.has(p.id));
  const pairedGross = selectedItems.reduce((s, p) => s + p.price, 0);
  const pairedNet = selectedItems.length >= 1 ? pairedGross * 0.9 : pairedGross;
  const pairedSavings = pairedGross - pairedNet;
  const handleAdd = () => {
    addItem(product.id, 1);
    selectedItems.forEach(p => addItem(p.id, 1));
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };
  return <div className="mt-5 rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1670">
      {/* Header — value prop */}
      <div className="px-5 pt-5 pb-4 border-b border-border" data-id="element-1671">
        <div className="flex items-start justify-between gap-3" data-id="element-1672">
          <div className="flex-1 min-w-0" data-id="element-1673">
            <div className="flex items-center gap-2" data-id="element-1674">
              <Sparkles className="h-3.5 w-3.5 text-primary flex-shrink-0" strokeWidth={1.75} data-id="element-1675" />
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-primary" data-id="element-1676">
                Stack &amp; save
              </span>
            </div>
            <h3 className="mt-2 text-[16px] font-semibold tracking-tight text-foreground leading-snug" data-id="element-1677">
              Researchers running {product.name} typically pair it with these.
            </h3>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.05em] font-bold text-primary [font-feature-settings:'tnum'] whitespace-nowrap mt-0.5 px-2 py-1 rounded-full bg-primary/10" data-id="element-1678">
            −10%
          </span>
        </div>

        {/* Benefits row */}
        <ul className="mt-3.5 grid sm:grid-cols-3 gap-2.5" data-id="element-1679">
          {[{
          label: 'Synergistic protocols',
          hint: 'Shared research goals'
        }, {
          label: 'Save 10% off the stack',
          hint: 'Applied automatically'
        }, {
          label: 'One cold-chain shipment',
          hint: 'Ships together, one box'
        }].map(b => <li key={b.label} className="flex items-start gap-2" data-id="element-1680">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-px" strokeWidth={2.5} data-id="element-1681" />
              <div className="min-w-0" data-id="element-1682">
                <div className="text-[12px] font-medium text-foreground leading-tight" data-id="element-1683">
                  {b.label}
                </div>
                <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-tight" data-id="element-1684">
                  {b.hint}
                </div>
              </div>
            </li>)}
        </ul>
      </div>

      {/* Paired peptide list */}
      <div className="px-5 py-4 flex flex-col gap-2" data-id="element-1685">
        {paired.map(p => {
        const checked = selected.has(p.id);
        return <button key={p.id} onClick={() => toggle(p.id)} className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left ${checked ? 'border-primary/40 bg-primary/[0.04]' : 'border-border bg-background hover:border-foreground/25'}`} aria-pressed={checked} data-id="element-1686">
              <span className={`relative h-[18px] w-[18px] rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'bg-primary border-primary text-primary-foreground' : 'border-border bg-background group-hover:border-foreground/40'}`} aria-hidden data-id="element-1687">
                {checked && <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1688">
                    <path d="M2 6.5 L5 9 L10 3.5" data-id="element-1689" />
                  </svg>}
              </span>
              <div className="flex-1 min-w-0" data-id="element-1690">
                <div className="text-[13px] font-semibold tracking-tight text-foreground truncate" data-id="element-1691">
                  {p.name}
                </div>
                <div className="font-mono text-[10.5px] text-muted-foreground mt-0.5 [font-feature-settings:'tnum']" data-id="element-1692">
                  <span className="text-foreground" data-id="element-1693">{p.doseMg}</span>
                  <span className="text-primary font-semibold" data-id="element-1694">mg</span>
                  <span className="text-border mx-1" data-id="element-1695">·</span>
                  <span className="text-primary" data-id="element-1696">
                    {p.purity.toFixed(1)}%
                  </span>{' '}
                  HPLC
                </div>
              </div>
              <span className="font-mono text-[13px] font-medium text-foreground flex-shrink-0 [font-feature-settings:'tnum']" data-id="element-1697">
                £{p.price}
              </span>
            </button>;
      })}
      </div>

      {/* Total + add */}
      {selectedItems.length > 0 && <div className="px-5 py-4 border-t border-border bg-secondary/30 flex items-center justify-between gap-3" data-id="element-1698">
          <div className="text-[11.5px] text-muted-foreground leading-snug" data-id="element-1699">
            <span className="font-semibold text-foreground" data-id="element-1700">
              {selectedItems.length}
            </span>{' '}
            paired {selectedItems.length === 1 ? 'peptide' : 'peptides'} added ·{' '}
            <span className="font-mono font-semibold text-primary [font-feature-settings:'tnum']" data-id="element-1701">
              save £{pairedSavings.toFixed(0)}
            </span>
          </div>
          <button onClick={handleAdd} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-primary text-primary-foreground text-[13px] font-semibold tracking-tight transition-all [box-shadow:0_1px_0_0_rgba(255,255,255,0.18)_inset,0_1px_2px_rgba(20,60,30,0.15),0_3px_8px_-3px_rgba(20,60,30,0.30)] hover:-translate-y-px hover:[box-shadow:0_1px_0_0_rgba(255,255,255,0.22)_inset,0_2px_4px_rgba(20,60,30,0.18),0_8px_18px_-5px_rgba(20,60,30,0.42)] active:translate-y-0" data-id="element-1702">
            {added ? <>
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} data-id="element-1703" />
                Added to cart
              </> : <>
                <ShoppingBag className="h-3.5 w-3.5" strokeWidth={2} data-id="element-1704" />
                Add stack · £{pairedNet.toFixed(0)}
              </>}
          </button>
        </div>}
    </div>;
}
function BundleSection({
  product
}: {
  product: (typeof PRODUCTS)[number];
}) {
  const {
    addItem
  } = useCart();
  const paired = useMemo(() => PRODUCTS.filter(p => p.id !== product.id && p.goals.some(g => product.goals.includes(g)) && p.inStock).slice(0, 2), [product.id, product.goals]);
  const allItems = useMemo(() => [product, ...paired], [product, paired]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(allItems.map(p => p.id)));
  const [added, setAdded] = useState(false);
  if (paired.length === 0) return null;
  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const selectedItems = allItems.filter(p => selected.has(p.id));
  const gross = selectedItems.reduce((s, p) => s + p.price, 0);
  const bundleDiscount = selectedItems.length >= 2 ? 0.1 : 0;
  const net = gross * (1 - bundleDiscount);
  const savings = gross - net;
  const handleAdd = () => {
    selectedItems.forEach(p => addItem(p.id, 1));
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };
  return <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1705">
      {/* Header — restrained */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border" data-id="element-1706">
        <div className="flex items-center gap-2.5" data-id="element-1707">
          <Sparkles className="h-4 w-4 text-primary flex-shrink-0" strokeWidth={1.75} data-id="element-1708" />
          <div data-id="element-1709">
            <div className="text-[13.5px] font-semibold tracking-tight text-foreground leading-tight" data-id="element-1710">
              Build your research stack
            </div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5" data-id="element-1711">
              Add 2+ paired peptides, save{' '}
              <span className="text-primary font-semibold" data-id="element-1712">10%</span>{' '}
              automatically
            </div>
          </div>
        </div>
      </div>

      <ul className="divide-y divide-border" data-id="element-1713">
        {allItems.map((p, i) => {
        const checked = selected.has(p.id);
        const isCurrent = p.id === product.id;
        return <li key={p.id} className="flex items-center gap-3 px-5 py-3" data-id="element-1714">
              <button onClick={() => toggle(p.id)} className={`relative h-[18px] w-[18px] rounded-md border flex items-center justify-center transition-colors flex-shrink-0 ${checked ? 'bg-foreground border-foreground text-background' : 'border-border bg-card hover:border-foreground/40'}`} aria-label={`${checked ? 'Remove' : 'Add'} ${p.name} from bundle`} aria-pressed={checked} data-id="element-1715">
                {checked && <CheckCircle2 className="h-3 w-3" strokeWidth={3} data-id="element-1716" />}
              </button>

              <div className="h-12 w-10 rounded-md bg-secondary/50 border border-border flex items-center justify-center flex-shrink-0 text-foreground" data-id="element-1717">
                <Vial label="" doseMg={p.doseMg} volumeMl={p.volumeMl} category={p.category} size={36} showShadow={false} showHighlight={false} data-id="element-1718" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-1719">
                <div className="flex items-center gap-2" data-id="element-1720">
                  <span className="text-[13.5px] font-semibold tracking-tight text-foreground truncate" data-id="element-1721">
                    {p.name}
                  </span>
                  {isCurrent && <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground border border-border rounded-full px-1.5 py-px" data-id="element-1722">
                      This item
                    </span>}
                </div>
                <div className="font-mono text-[11px] text-muted-foreground mt-0.5 [font-feature-settings:'tnum']" data-id="element-1723">
                  {p.doseMg}mg ·{' '}
                  <span className="text-primary" data-id="element-1724">{p.purity.toFixed(1)}%</span>
                </div>
              </div>

              <span className="font-mono text-[14px] font-medium text-foreground whitespace-nowrap [font-feature-settings:'tnum']" data-id="element-1725">
                £{p.price}
              </span>
            </li>;
      })}
      </ul>

      {/* Bundle savings callout — appears only when discount active */}
      {savings > 0 && <div className="px-5 py-3 bg-primary text-primary-foreground flex items-center justify-between gap-3" data-id="element-1726">
          <div className="flex items-center gap-2" data-id="element-1727">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" strokeWidth={2.5} data-id="element-1728" />
            <span className="text-[13px] font-semibold tracking-tight" data-id="element-1729">
              You're saving £{savings.toFixed(0)} today
            </span>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-wider font-bold opacity-90 [font-feature-settings:'tnum']" data-id="element-1730">
            {selectedItems.length} items · 10% off
          </span>
        </div>}

      {/* Total + CTA */}
      <div className="px-5 py-4 border-t border-border bg-secondary/30 flex items-center justify-between gap-4" data-id="element-1731">
        <div data-id="element-1732">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1733">
            Bundle total ({selectedItems.length}{' '}
            {selectedItems.length === 1 ? 'item' : 'items'})
          </div>
          <div className="flex items-baseline gap-2 mt-1" data-id="element-1734">
            {savings > 0 && <span className="font-mono text-[13px] text-muted-foreground line-through [font-feature-settings:'tnum']" data-id="element-1735">
                £{gross.toFixed(0)}
              </span>}
            <span className={`font-mono text-[26px] font-medium leading-none tracking-[-0.015em] [font-feature-settings:'tnum'] ${savings > 0 ? 'text-primary' : 'text-foreground'}`} data-id="element-1736">
              £{net.toFixed(0)}
            </span>
          </div>
          {savings === 0 && selectedItems.length === 1 && paired.length > 0 && <div className="text-[10.5px] text-muted-foreground mt-1" data-id="element-1737">
              Select 1 more to unlock{' '}
              <span className="text-primary font-semibold" data-id="element-1738">10% off</span>
            </div>}
        </div>
        <CTA variant="primary" size="md" onClick={handleAdd} disabled={selectedItems.length === 0} icon={added ? <CheckCircle2 className="h-[14px] w-[14px]" strokeWidth={2.25} data-id="element-1740" /> : <ShoppingBag className="h-[14px] w-[14px]" strokeWidth={2} data-id="element-1741" />} iconPosition="left" data-id="element-1739">
          {added ? 'Added' : selectedItems.length === 0 ? 'Select items' : selectedItems.length === 1 ? 'Add to cart' : `Add stack · £${net.toFixed(0)}`}
        </CTA>
      </div>
    </div>;
}